/** Dashboard — port of the original views.dashboard. Figures come from domain/dashboard.ts. */
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import { fmtDate, fmtH } from '@/domain/format';
import { dashboardData } from '@/domain/dashboard';
import { useDataset } from '@/hooks/data';
import { PermissionGate } from '@/auth/ProtectedRoute';
import { AgoCell, DeadlineCell, ErrorState, IconButton, LoadingState, MemberLink, NoRecords, ProjectLinks, StatCard, StatusBadge, Topbar } from '@/ui/kit';
import { useAppActions } from '../actions';
import { useAuth } from '@/app/AppContext';

export function DashboardPage() {
  const ds = useDataset();
  const nav = useNavigate();
  const act = useAppActions();
  const auth = useAuth();
  const d = useMemo(() => (ds.data ? dashboardData(ds.data) : null), [ds.data]);
  const header = (sub?: string) => (
    <Topbar title="Dashboard" sub={sub}>
      <button className="btn" onClick={() => nav('/daily-report')}>
        Daily Report
      </button>
      <PermissionGate perm="updates:write">
        <button className="btn" onClick={() => act.projectLog()}>
          + Project Log Work
        </button>
      </PermissionGate>
      <PermissionGate perm="worklog:write">
        <button className="btn primary" onClick={() => act.workLog()}>
          + Employee Log Work
        </button>
      </PermissionGate>
    </Topbar>
  );
  if (ds.error) return (<>{header()}<ErrorState error={ds.error} onRetry={ds.refetch} /></>);
  if (!ds.data || !d) return (<>{header()}<LoadingState /></>);
  const { projects, members } = ds.data;
  const pname = (id: string) => (id ? projects.find((p) => p.id === id)?.name || '—' : 'General');
  const maxH = d.people[0]?.hours || 1;
  const setDeadline = auth.can('projects:write') ? (id: string) => act.deadline(id) : undefined;
  return (
    <>
      {header(`${projects.length} projects · ${members.length} members · last data ${d.lastDate ? fmtDate(d.lastDate) : '—'}`)}
      <div className="cards">
        <StatCard to="/projects" n={d.activeProjects} suffix={` / ${projects.length}`} label="Active projects" />
        <StatCard to="/projects?f=overdue" n={d.overdue.length} color={d.overdue.length ? 'var(--danger)' : 'inherit'} label="Past deadline" />
        <StatCard to="/projects?f=stale" n={d.stale.length} color={d.stale.length ? 'var(--warn)' : 'inherit'} label="No project log in 2+ days" />
        <StatCard to="/work-log" n={fmtH(d.hours7)} label="Hours, last 7 days" />
        <StatCard to={`/work-log?d=${d.lastDate}`} n={fmtH(d.hoursOnLastDate)} label={`Hours on ${d.lastDate ? fmtDate(d.lastDate) : '—'}`} />
        <StatCard to="/members" n={d.people.length} suffix={` / ${members.length}`} label="Members active, last 7 days" />
      </div>
      <div className="panel">
        <div className="panel-h">
          <h2>Projects</h2>
          <span className="hint">
            Amber = active project with no Project Log for 2+ days · <Link to="/projects">Manage →</Link>
          </span>
        </div>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Project</th>
                <th>Status</th>
                <th>Lead</th>
                <th>Deadline</th>
                <th className="num">Hours (7d)</th>
                <th>Last project log</th>
                <th>Links</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.length ? (
                d.rows.map(({ project: p, hours7, lastUpdate, active }) => (
                  <tr key={p.id}>
                    <td>
                      <Link to={`/projects/${encodeURIComponent(p.id)}`} style={{ fontWeight: 600, textDecoration: 'none' }}>
                        {p.name}
                      </Link>
                      <div className="hint">{p.service}</div>
                    </td>
                    <td>
                      <StatusBadge status={p.status} />
                    </td>
                    <td>{p.lead || '—'}</td>
                    <td>
                      <DeadlineCell project={p} onSet={setDeadline && (() => setDeadline(p.id))} />
                    </td>
                    <td className="num">{hours7 ? fmtH(hours7) : <span className="muted">0 h</span>}</td>
                    <td>
                      <AgoCell iso={lastUpdate} flagAfter={active ? 2 : null} />
                    </td>
                    <td className="link-cell">
                      <ProjectLinks project={p} />
                    </td>
                    <td>
                      <IconButton icon="view" tip="View" onClick={() => nav(`/projects/${encodeURIComponent(p.id)}`)} />
                    </td>
                  </tr>
                ))
              ) : (
                <NoRecords cols={8} />
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="panel">
        <div className="panel-h">
          <h2>Who worked, last 7 days</h2>
          <Link to="/members" className="hint">
            All members →
          </Link>
        </div>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Member</th>
                <th>Role</th>
                <th>Projects</th>
                <th className="num">Days</th>
                <th style={{ width: '30%' }}>Hours</th>
                <th className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              {d.people.length ? (
                d.people.map((x) => (
                  <tr key={x.member.id}>
                    <td>
                      <MemberLink id={x.member.id} name={x.member.name} />
                    </td>
                    <td className="muted">{x.member.role || ''}</td>
                    <td>{x.projectIds.map(pname).join(', ')}</td>
                    <td className="num">{x.days}</td>
                    <td>
                      <div className="bar">
                        <i style={{ width: `${(x.hours / maxH) * 100}%` }} />
                      </div>
                    </td>
                    <td className="num">{fmtH(x.hours)}</td>
                  </tr>
                ))
              ) : (
                <NoRecords cols={6} />
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
